<template>
  <div
    class="product-swatches"
    :class="[
      $blockClass(blockClass, 'product-swatches'),
    ]"
  >
    <p
      v-if="showOnlyText"
      class="product-swatches__text text-desktop-12-desktop text-mobile-12-mobile"
    >
      <template v-if="productSwatches.length > 1">
        <span

          v-text="$string('product.swatches.colors.other',
                          { replace: { count: (productSwatches.length) } }
          )"
        />
      </template>
    </p>
    <ul
      v-else-if="productSwatches && hasMultipleSwatches"
      class="product-swatches__list"
      :class="[
        $blockClass(blockClass, 'product-swatches-list'),
      ]"
    >
      <template
        v-for="(productSwatch, index) of productSwatches"
        :key="`product-swatch-${index}-${productSwatch.handle}`"
      >
        <li
          v-if="(index + 1) <= swatchLimit || !swatchLimit"
          class="product-swatches__item"
        >
          <a
            class="product-swatches__swatch"
            :class="[
              `product-swatches__swatch--${getHandleizedColor(productSwatch)}`,
              (product.id === productSwatch?.id && showActiveSwatch) &&
                `product-swatches__swatch--is-active`
            ]"
            :style="
              // eslint-disable-next-line max-len
              `--swatch-color: var(--theme-color-${getHandleizedColor(productSwatch)})`"

            :aria-label="
              productSwatch.title"
            :href="productSwatch.url"
            @click="handleGtm"
          />

          <div
            v-if="showHoverImage"
            class="product-swatches__hover-image"
          >
            <responsive-image
              :image="getSwatchImage(productSwatch)"
              min-max="500-2000"
              sizes="(max-width: 767px) 100vw, (min-width: 768px) calc(100vw / 4)"
              ratio="5:7"
            />
          </div>
        </li>
      </template>
      <li
        v-if="productSwatches.length > swatchLimit && swatchLimit"
        class="
          product-swatches__item
          product-swatches__item--text
          text-desktop-12-desktop
          text-mobile-12-mobile
        "
        v-text="`+ ${productSwatches.length - swatchLimit}`"
      />
    </ul>
  </div>
</template>

<script>

/**
 * Vue: Product swatches (product-swatches)
 * -----------------------------------------------------------------------------
 * Component to enable selection between related product colorways.
 *
 * @param {Boolean, Object} product - Shopify product object.
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Boolean} [loadSwatches] - Whether to load the component products.
 * @param {Boolean} [showOnlyText] - Show only text instead of swatches
 * @param {Boolean, Number} [swatchLimit] - Number of visible swatches.
 * @param {Boolean} [showActiveSwatch] - Whether to show the active swatch.
 * @param {Boolean} [showHoverImage] - Whether to show image on hover.
 */

import products from '~/graphql/queries/products.gql'
import { formatProduct } from '~/helpers/format'

import ResponsiveImage from '~global/images/responsive-image'

export default {
  name: 'ProductSwatches',

  components: {
    ResponsiveImage,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    loadSwatches: {
      type: Boolean,
      default: false,
    },
    showOnlyText: {
      type: Boolean,
      default: false,
    },
    swatchLimit: {
      type: [Boolean, Number],
      default: false,
    },
    showActiveSwatch: {
      type: Boolean,
      default: true,
    },
    showHoverImage: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'product-swatches': (payload) => {
      return Array.isArray(payload) || !payload
    },
  },

  data() {
    return {
      hoverProduct: null,
      productSwatches: [],
      hideProductSwatches: false,
      colorFamily: null,
    }
  },
  computed: {

    /**
     * Returns the color of the currently selected product.
     * @returns {Boolean, String}
     */
    productColor() {
      if (this.hoverProduct) {
        const hoverColor = this.getColorFromTag(this.hoverProduct)
        if (hoverColor) {
          return hoverColor.split(':')[1].trim()
        }
      }

      const colorTag = this.getColorFromTag(this.product)
      if (!colorTag) {
        return false
      }

      return colorTag.split(':')[1].trim()
    },

    /**
     * Returns whether there is more than 1 swatch.
     * @returns {Boolean}
     */
    hasMultipleSwatches() {
      return this.productSwatches?.length > 1
    },
  },

  watch: {
    product: {
      immediate: true,
      handler() {
        this.fetchColorProducts()
      },
    },
    loadSwatches: {
      handler() {
        this.fetchColorProducts()
      },
    },
  },

  methods: {

    /**
     * Main init function to kick off functionality.
     */
    async fetchColorProducts() {
      if (!this.loadSwatches) {
        return
      }

      /**
      * Check if swatches are cached in session storage. Otherwise, fetch
      * swatches from GraphQL.
      */
      if (this.getCachedSwatches()) {
        this.productSwatches = this.getCachedSwatches()
      } else {
        try {
          const colorFamily = await this.getColorFamilyTag()

          await this.getProductsByTag(colorFamily)
        } catch (error) {
          throw new Error('Failed to load color family', error)
        }
      }
    },

    /**
     * Returns the color family tag from the product tags.
     * @returns {Promise<String>}
     */
    getColorFamilyTag() {
      return new Promise((resolve) => {
        if (!this.product) {
          resolve()
        }

        const colorTag = this.product.tags.find(
          tag => tag.includes('product-family:'),
        )

        resolve(colorTag)
      })
    },

    /**
     * Loads linked size product from GraphQL and caches the result in
     * session storage to prevent new requests when filtering.
     * @param {String} tag - Tag to query products by.
     */
    async getProductsByTag(tag) {
      if (!tag) {
        return
      }

      const escapedTag = tag.replace(/[.*+?:^${}()|[\]\\]/g, '\\$&')
      const tagString = `tag:'${escapedTag}'`

      try {
        const response = await cnvs.Query({
          query: products,
          variables: {
            country: cnvs.store.country,
            query: tagString,
            language: cnvs.store.language,
          },
        })

        if (!response?.products?.edges?.length) {
          this.$emit('product-swatches', null)
          return
        }

        const formattedResponse =
          response.products.edges.map(edge => formatProduct({ product: edge.node }))
        const currentProductIndex =
          formattedResponse.findIndex(
            (colorProduct) => colorProduct.handle === this.product?.handle,
          )

        if (currentProductIndex !== -1) {
          const currentProduct = formattedResponse.splice(currentProductIndex, 1)[0]
          formattedResponse.unshift(currentProduct)
        }

        this.productSwatches = formattedResponse

        this.cacheProductSwatches(formattedResponse)

        this.$emit('product-swatches', formattedResponse)

      } catch (error) {
        this.$emit('product-swatches', null)

        throw new Error(error)
      }

    },

    /**
     * Returns the last product image to use for the colorway swatch.
     * @param {Object} product - Shopify product object.
     * @return {Object}
     */
    getSwatchImage(product) {
      return product.images?.[product.images?.length - 1] ?? false
    },

    /**
     * Returns the color of the provided product from its tag.
     * @param {Object} product - Shopify product object.
     * @returns {Object, Undefined}
     */
    getColorFromTag(product) {
      return product?.tags?.find(tag => tag.includes('color:'))
    },

    /**
     * Returns the color in a handleized format.
     * @param {Object} product - Shopify product object.
     * @returns {String}
     */
    getHandleizedColor(product) {
      const color = product.swatchColor ? product.swatchColor : product.filterColor

      return color?.trim().toLowerCase().replace(' ', '-')
    },

    /**
     * Returns cached swatches from session storage if they exist.
     * @returns {Object, Boolean}
     */
    getCachedSwatches() {
      if (!sessionStorage) {
        return false
      }

      const cachedSwatches = sessionStorage.getItem('productSwatches') ?? false
      const existingSwatchIndex = cachedSwatches
        ? JSON.parse(cachedSwatches)?.findIndex(item => item.id === this.product?.id)
        : false

      if (!cachedSwatches || existingSwatchIndex === -1) {
        return false
      }

      return JSON.parse(cachedSwatches)[existingSwatchIndex]?.content
    },

    /**
    * Cache swatches in session storage to prevent new requests when
    * filtering on a PLP
    * @param {Object} productSwatches - Product swatches object.
    */
    cacheProductSwatches(productSwatches) {
      if (!sessionStorage) {
        return
      }

      const swatchesObject = {
        id: this.product?.id,
        content: productSwatches,
      }

      let cachedSwatches = sessionStorage.getItem('productSwatches')

      if (cachedSwatches) {
        cachedSwatches = JSON.parse(cachedSwatches)
        cachedSwatches.push(swatchesObject)
      } else {
        cachedSwatches = [swatchesObject]
      }

      sessionStorage.setItem('productSwatches', JSON.stringify(cachedSwatches))
    },

    handleGtm() {
      const dataLayer = window.dataLayer || []
      const { handle, id, price, title } = this.product
      const url = document.location.pathname
      const template = url.split('/')[1]

      dataLayer.push({
        event: 'product_swatch_click',
        productSwatchClick: {
          template,
          url,
          item: {
            handle,
            id,
            title,
            price: price.amount,
            currency: price.currencyCode,
          },
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import './product-swatches';
</style>
